import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TABS = [["richiesto", "In attesa"], ["confermato", "Confermati"], ["svolto", "Svolti"], ["rifiutato", "Non accolti"], ["annullato", "Annullati"], ["all", "Tutti"]];
const TONE = { richiesto: "bg-amber-100 text-amber-800", confermato: "bg-emerald-100 text-emerald-800", rifiutato: "bg-red-100 text-red-700", svolto: "bg-stone-200 text-stone-700", annullato: "bg-stone-100 text-stone-500" };
const dt = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

function ManageDialog({ m, onClose }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ scheduled_at: m.scheduled_at ? m.scheduled_at.slice(0, 16) : "", where: m.where || "", staff_note: m.staff_note || "" });
  const save = useMutation({
    mutationFn: async (status) => (await api.patch(`/tutor-area/meetings/${m.id}`, { status, ...f, scheduled_at: f.scheduled_at || null })).data,
    onSuccess: (d) => { toast.success(`Colloquio: ${d.status_label}`); qc.invalidateQueries({ queryKey: ["meetings"] }); onClose(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="meeting-manage-dialog">
        <DialogHeader>
          <DialogTitle>Colloquio · {m.student_name}</DialogTitle>
          <DialogDescription>{m.mode_label} · tutor {m.tutor_name || "non assegnato"} · preferenza: {m.preferred}</DialogDescription>
        </DialogHeader>
        {m.reason && <div className="text-sm bg-stone-50 border border-stone-200 rounded-lg p-3">{m.reason}</div>}
        <div className="space-y-3">
          <div><Label className="text-xs">Data e ora *</Label><Input type="datetime-local" value={f.scheduled_at} onChange={set("scheduled_at")} className="mt-1" data-testid="meeting-datetime"/></div>
          <div><Label className="text-xs">{m.mode === "video" ? "Link videochiamata" : m.mode === "telefono" ? "Numero / chi chiama" : "Luogo"}</Label><Input value={f.where} onChange={set("where")} className="mt-1" data-testid="meeting-where"/></div>
          <div><Label className="text-xs">Nota per lo studente</Label><Textarea rows={2} value={f.staff_note} onChange={set("staff_note")} className="mt-1" data-testid="meeting-note"/></div>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={() => save.mutate("rifiutato")} disabled={save.isPending} data-testid="meeting-reject">Non accogliere</Button>
          {m.status === "confermato" && <Button variant="outline" onClick={() => save.mutate("svolto")} disabled={save.isPending} data-testid="meeting-done">Segna svolto</Button>}
          <Button className="bg-[#1C352D] hover:bg-[#14271f]" onClick={() => save.mutate("confermato")} disabled={save.isPending || !f.scheduled_at} data-testid="meeting-confirm">Conferma e avvisa</Button>
        </div>
        <p className="text-[11px] text-stone-500">Alla conferma studente e tutor ricevono un'email; lo studente anche una notifica.</p>
      </DialogContent>
    </Dialog>
  );
}

export default function Colloqui() {
  const [tab, setTab] = useState("richiesto");
  const [open, setOpen] = useState(null);
  const { data } = useQuery({ queryKey: ["meetings", tab], queryFn: async () => (await api.get("/tutor-area/meetings", { params: { status: tab } })).data });
  const items = data?.items || [];
  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1100px] mx-auto space-y-5" data-testid="meetings-page">
      <PageHeader title="Colloqui con il tutor" subtitle="Richieste degli studenti della Scuola Modulare: fissa data, ora e modalità."/>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          {TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`meetings-tab-${v}`}>{l}{v !== "all" && data?.counts?.[v] ? ` (${data.counts[v]})` : ""}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <Card className="border border-stone-200 rounded-xl shadow-none divide-y divide-stone-100">
        {items.map((m) => (
          <button key={m.id} onClick={() => setOpen(m)} className="w-full text-left p-4 flex flex-wrap items-start gap-3 hover:bg-stone-50 transition-colors" data-testid={`meeting-row-${m.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[#1C352D]">{m.student_name} <span className="text-xs text-stone-500 font-normal">{m.year_level ? `· ${m.year_level}° anno` : ""} · tutor {m.tutor_name || "—"}</span></div>
              <div className="text-sm text-stone-600">{m.mode_label} · {m.status === "confermato" && m.scheduled_at ? dt(m.scheduled_at) : `preferenza: ${m.preferred}`}</div>
              {m.reason && <div className="text-xs text-stone-500 truncate">{m.reason}</div>}
            </div>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${TONE[m.status]}`}>{m.status_label}</span>
          </button>
        ))}
        {!items.length && <div className="p-8 text-center text-sm text-stone-400">Nessun colloquio.</div>}
      </Card>
      {open && <ManageDialog m={open} onClose={() => setOpen(null)}/>}
    </div>
  );
}
